import React from 'react';
import { Space } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { ServiceRequestModel } from '@/typings/models/ServiceRequest';
import { ServiceModel } from '@/typings/models/Location';
import { BranchActionTag, LeadActionTag } from '@/components/ActionTag';
import ServiceRequestAssignee from '@/components/Lead/ServiceRequestAssignee';
import ServiceRequestStatusTag from '@/components/Lead/ServiceRequestStatusTag';
import { FormatDate } from '@/components/PrettyDate';
import styles from '@/components/Kanban/styles.less';
import { APPOINTMENT_PROPERTIES } from '@/components/Kanban/VisibilitySettings/constants';
import { INTL_ATTRIBUTE, INTL_ENTITY } from '@/utils/intl';

/**
 * Configuration for appointment table columns.
 * Defines titles and render functions for each customizable property.
 */
export const APPOINTMENT_PROPERTY_COLUMN_SETTINGS = {
  [APPOINTMENT_PROPERTIES.TIME]: {
    title: { id: 'common.attribute.time', defaultMessage: 'Time' },
    fixed: 'left',
    width: 150,
    render: (_: any, serviceRequest: ServiceRequestModel) => (
      <div style={{ whiteSpace: 'nowrap' }}>
        <div style={{ lineHeight: 1.2 }}>
          <FormatDate date={serviceRequest.utc_start_time} format={'ddd, DD MMM YYYY'} />
        </div>
        <small>
          <Space size={2}>
            <FormatDate onlyTime date={serviceRequest.utc_start_time} />
            <span>-</span>
            <FormatDate onlyTime date={serviceRequest.utc_end_time} />
          </Space>
        </small>
      </div>
    ),
  },
  [APPOINTMENT_PROPERTIES.TITLE]: {
    title: { id: 'common.attribute.title', defaultMessage: 'Title' },
    dataIndex: 'title',
    render: (_: ServiceModel, serviceRequest: ServiceRequestModel) =>
      serviceRequest.title || serviceRequest.branch_service?.service_type?.name,
  },
  [APPOINTMENT_PROPERTIES.BRANCH]: {
    title: { id: 'entity.branch.singular', defaultMessage: 'Branch' },
    width: 320,
    dataIndex: 'branch_service',
    render: (_: ServiceModel, serviceRequest: ServiceRequestModel) => (
      <span onClick={(e) => e.stopPropagation()}>
        <Space direction={'vertical'} size={'small'} className={styles.appointmentTableBranch}>
          {serviceRequest.branch_service?.branch && (
            <Space size={2}>
              <EnvironmentOutlined />
              <BranchActionTag size={'small'} branch={serviceRequest.branch_service?.branch} showAddressTooltip />
            </Space>
          )}
        </Space>
      </span>
    ),
  },
  [APPOINTMENT_PROPERTIES.LEAD]: {
    title: { id: 'entity.lead.singular', defaultMessage: 'Customer Project' },
    dataIndex: 'lead',
    render: (lead: any) => (
      <span onClick={(e) => e.stopPropagation()}>
        <LeadActionTag lead={lead} size={'large'} />
      </span>
    ),
  },
  [APPOINTMENT_PROPERTIES.LEAD_SOURCE]: {
    title: { id: 'pages.lead.fields.leadSource', defaultMessage: 'Lead Source' },
    dataIndex: ['lead', 'lead_source', 'name'],
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.lead?.lead_source?.name || '-',
  },
  [APPOINTMENT_PROPERTIES.AGENT]: {
    title: { id: 'entity.agent.singular', defaultMessage: 'Agent' },
    dataIndex: 'agent',
    render: (_: any, serviceRequest: ServiceRequestModel) => (
      <span onClick={(e) => e.stopPropagation()}>
        <ServiceRequestAssignee serviceRequest={serviceRequest} />
      </span>
    ),
  },
  [APPOINTMENT_PROPERTIES.STATUS]: {
    title: { id: 'common.attribute.status', defaultMessage: 'Status' },
    dataIndex: 'status',
    render: (status: any) => <ServiceRequestStatusTag fontSize={10} status={status} />,
  },
  [APPOINTMENT_PROPERTIES.SERVICE_TYPE]: {
    title: { id: 'entity.service_type.singular', defaultMessage: 'Service Type' },
    dataIndex: 'branch_service',
    render: (_: ServiceModel, serviceRequest: ServiceRequestModel) =>
      serviceRequest.branch_service?.service_type?.name || '-',
  },
  [APPOINTMENT_PROPERTIES.CREATED_AT]: {
    title: { id: 'common.attribute.created_at', defaultMessage: 'Created Date' },
    dataIndex: 'created',
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.created ? <FormatDate date={serviceRequest.created} /> : '-',
  },
  [APPOINTMENT_PROPERTIES.UPDATED_AT]: {
    title: { id: 'common.attribute.updated_at', defaultMessage: 'Updated Date' },
    dataIndex: 'modified',
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.modified ? <FormatDate date={serviceRequest.modified} /> : '-',
  },
  [APPOINTMENT_PROPERTIES.PROJECT_CREATED_AT]: {
    title: { entity: INTL_ENTITY.LEAD, attribute: INTL_ATTRIBUTE.CREATED_AT },
    dataIndex: ['lead', 'created'],
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.lead?.created ? <FormatDate date={serviceRequest.lead.created} /> : '-',
  },
  [APPOINTMENT_PROPERTIES.PROJECT_UPDATED_AT]: {
    title: { entity: INTL_ENTITY.LEAD, attribute: INTL_ATTRIBUTE.UPDATED_AT },
    dataIndex: ['lead', 'modified'],
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.lead?.modified ? <FormatDate date={serviceRequest.lead.modified} /> : '-',
  },
  [APPOINTMENT_PROPERTIES.CUSTOMER_CREATED_AT]: {
    title: { entity: INTL_ENTITY.CUSTOMER, attribute: INTL_ATTRIBUTE.CREATED_AT },
    dataIndex: ['lead', 'customer', 'created'],
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.lead?.customer?.created ? <FormatDate date={serviceRequest.lead.customer.created} /> : '-',
  },
  [APPOINTMENT_PROPERTIES.CUSTOMER_MODIFIED_AT]: {
    title: { entity: INTL_ENTITY.CUSTOMER, attribute: INTL_ATTRIBUTE.UPDATED_AT },
    dataIndex: ['lead', 'customer', 'modified'],
    render: (_: any, serviceRequest: ServiceRequestModel) =>
      serviceRequest.lead?.customer?.modified ? <FormatDate date={serviceRequest.lead.customer.modified} /> : '-',
  },
};
